/**
 * A Word file's package (Open Packaging Conventions; part 18 log, table O): its content types, its relationships and
 * where each points. Only the parts a row of the converter names are ever read; nothing outside the package is ever
 * fetched (O5). Part names are looked up without case, as OPC compares them.
 */
import type { ZipArchive } from '../../archive/zip.ts';
import { ConversionError } from '../problems.ts';
import { attribute, readXml, xmlBytes } from '../xml.ts';

/** The namespaces the converter reads (X8 maps Strict OOXML onto these). */
export const NS = Object.freeze({
  w: 'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
  r: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
  wp: 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing',
  a: 'http://schemas.openxmlformats.org/drawingml/2006/main',
  pic: 'http://schemas.openxmlformats.org/drawingml/2006/picture',
  m: 'http://schemas.openxmlformats.org/officeDocument/2006/math',
  v: 'urn:schemas-microsoft-com:vml',
  mc: 'http://schemas.openxmlformats.org/markup-compatibility/2006',
  relationships: 'http://schemas.openxmlformats.org/package/2006/relationships',
  contentTypes: 'http://schemas.openxmlformats.org/package/2006/content-types',
  dc: 'http://purl.org/dc/elements/1.1/',
});

/** O3: the main part's content types: a document or a template, with or without macros. */
export const MAIN_TYPES: ReadonlySet<string> = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml',
  'application/vnd.ms-word.document.macroEnabled.main+xml',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template.main+xml',
  'application/vnd.ms-word.template.macroEnabledTemplate.main+xml',
]);

/** A relationship: its kind (the type URI's last segment), and its target (a part's path, or a URL for an external one). */
export interface Relationship {
  readonly kind: string;
  readonly target: string;
  readonly external: boolean;
}

/**
 * O4: `target` resolved against the folder of the part at `source` (`''` for the package itself): `%xx` decoded, `.`
 * and `..` applied, a leading `/` from the root. Null when it climbs above the root or doesn't decode.
 */
export function resolveTarget(source: string, target: string): string | null {
  let decoded: string;
  try {
    decoded = decodeURIComponent(target);
  } catch {
    return null;
  }
  const folder = decoded.startsWith('/') ? [] : source.split('/').slice(0, -1);
  const segments = [...folder];
  for (const segment of decoded.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      if (segments.length === 0) return null;
      segments.pop();
    } else segments.push(segment);
  }
  return segments.length === 0 ? null : segments.join('/');
}

/** A part name's key: NFC, lower case (OPC compares names without case; part 17's N8 refuses two equal so). */
function partKey(path: string): string {
  return path.normalize('NFC').toLowerCase();
}

/** The relationships part of the part at `path`: `a/b.xml` → `a/_rels/b.xml.rels`, the package's `_rels/.rels`. */
function relationshipsPath(path: string): string {
  const slash = path.lastIndexOf('/');
  return `${path.slice(0, slash + 1)}_rels/${path.slice(slash + 1)}.rels`;
}

/** A Word file's parts, read on demand. */
export class DocxPackage {
  readonly #archive: ZipArchive;
  /** Each part's path by its key ({@link partKey}). */
  readonly #paths: ReadonlyMap<string, string>;
  readonly #sizes: ReadonlyMap<string, number>;
  readonly #onRead: (bytes: number) => void;

  constructor(archive: ZipArchive, onRead: (bytes: number) => void) {
    this.#archive = archive;
    this.#paths = new Map(archive.files.map((file) => [partKey(file.path), file.path]));
    this.#sizes = new Map(archive.files.map((file) => [file.path, file.size]));
    this.#onRead = onRead;
  }

  /** The archive's own path of the part `path` names, without case; null when there is none. */
  find(path: string): string | null {
    return this.#paths.get(partKey(path)) ?? null;
  }

  /** The unzipped size of the part at `path` (as {@link find} gives it). */
  size(path: string): number {
    return this.#sizes.get(path) ?? 0;
  }

  /** The bytes of the part `path` names, or null when there is none. */
  async read(path: string): Promise<Uint8Array | null> {
    const found = this.find(path);
    return found === null ? null : this.readPart(found);
  }

  /** The bytes of the part at `path`, as {@link find} gave it. */
  readPart(path: string): Promise<Uint8Array> {
    return this.#archive.read(path, this.#onRead);
  }

  /** O2, O4–O6: the relationships of the part at `path` (`''` for the package), by ID; none when it has no part. */
  async relationships(path: string): Promise<ReadonlyMap<string, Relationship>> {
    const out = new Map<string, Relationship>();
    const bytes = await this.read(path === '' ? '_rels/.rels' : relationshipsPath(path));
    if (bytes === null) return out;
    readXml(xmlBytes(bytes), {
      open: (element, depth) => {
        if (depth === 0) return 'descend';
        if (element.ns !== NS.relationships || element.name !== 'Relationship') return 'skip';
        const id = attribute(element, '', 'Id');
        const type = attribute(element, '', 'Type');
        const target = attribute(element, '', 'Target');
        if (id === undefined || type === undefined || target === undefined || out.has(id)) return 'skip';
        const external = attribute(element, '', 'TargetMode') === 'External';
        const resolved = external ? target : resolveTarget(path, target);
        if (resolved !== null) out.set(id, { kind: type.slice(type.lastIndexOf('/') + 1), target: resolved, external });
        return 'skip';
      },
    });
    return out;
  }

  /** O1–O3: the main part's path and whether it holds macros (O7); `not-docx` when the package isn't Word's. */
  async main(): Promise<{ readonly path: string; readonly macros: boolean }> {
    const types = await this.read('[Content_Types].xml');
    if (types === null) throw new ConversionError('not-docx', 'no content types');
    const overrides = new Map<string, string>();
    const defaults = new Map<string, string>();
    readXml(xmlBytes(types), {
      open: (element, depth) => {
        if (depth === 0) return 'descend';
        const type = attribute(element, '', 'ContentType');
        if (element.ns === NS.contentTypes && type !== undefined) {
          const part = attribute(element, '', 'PartName');
          const extension = attribute(element, '', 'Extension');
          if (element.name === 'Override' && part !== undefined) overrides.set(partKey(part.replace(/^\//u, '')), type);
          if (element.name === 'Default' && extension !== undefined) defaults.set(extension.toLowerCase(), type);
        }
        return 'skip';
      },
    });
    const documents = [...(await this.relationships('')).values()].filter((relationship) => relationship.kind === 'officeDocument' && !relationship.external);
    const first = documents[0];
    if (documents.length !== 1 || first === undefined) throw new ConversionError('not-docx', `${documents.length} main documents`);
    const path = this.find(first.target);
    if (path === null) throw new ConversionError('not-docx', 'no main document');
    const type = overrides.get(partKey(path)) ?? defaults.get(path.slice(path.lastIndexOf('.') + 1).toLowerCase());
    if (type === undefined || !MAIN_TYPES.has(type)) throw new ConversionError('not-docx', 'a main part that is not a Word document');
    return { path, macros: type.includes('macroEnabled') || this.find('word/vbaProject.bin') !== null };
  }
}
